import { useState, type MouseEvent as ReactMouseEvent } from 'react'
import { Typography, theme } from 'antd'
import { SCORE_MAX, SCORE_MIN, type TrendPoint } from './ratingApi'

interface Props {
  quarters: readonly string[]
  points: readonly TrendPoint[]
  color: string
  ariaLabel: string
  quarterLabel: (quarter: string) => string
  countLabel: (count: number) => string
  height?: number
}

const PAD_X = 4
const PAD_Y = 8
const TICKS = [5, 4, 3, 2, 1]

function xOf(index: number, total: number): number {
  return total <= 1 ? 50 : PAD_X + (index * (100 - 2 * PAD_X)) / (total - 1)
}

function yOf(value: number): number {
  return PAD_Y + ((SCORE_MAX - value) / (SCORE_MAX - SCORE_MIN)) * (100 - 2 * PAD_Y)
}

const ALL_TICKS_UP_TO = 8

/** Shkala 1–5 qotirilgan: 4,1 va 4,3 bir-biridan uzoq ko'rinmasin. Baholanmagan chorakda chiziq uziladi. */
export function RatingTrendChart({
  quarters,
  points,
  color,
  ariaLabel,
  quarterLabel,
  countLabel,
  height = 180,
}: Props) {
  const { token } = theme.useToken()
  const [hover, setHover] = useState<number | null>(null)

  const byQuarter = new Map(points.map((p) => [p.quarter, p]))
  const placed = quarters.map((quarter, index) => ({ quarter, index, point: byQuarter.get(quarter) ?? null }))

  let path = ''
  let previousPresent = false
  for (const { index, point } of placed) {
    if (point === null) {
      previousPresent = false
      continue
    }
    path += `${previousPresent ? 'L' : 'M'} ${xOf(index, quarters.length).toFixed(2)} ${yOf(point.average).toFixed(2)} `
    previousPresent = true
  }

  const handleMove = (e: ReactMouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = (e.clientX - rect.left) / rect.width
    setHover(Math.max(0, Math.min(quarters.length - 1, Math.round(ratio * (quarters.length - 1)))))
  }

  const hovered = hover === null ? null : placed[hover]
  const tickIndexes =
    quarters.length <= ALL_TICKS_UP_TO
      ? new Set(quarters.keys())
      : new Set([0, Math.floor((quarters.length - 1) / 2), quarters.length - 1])

  return (
    <div>
      <div style={{ display: 'flex', gap: 6 }}>
        <div style={{ position: 'relative', width: 16, height }}>
          {TICKS.map((tick) => (
            <span
              key={tick}
              style={{
                position: 'absolute',
                top: `${yOf(tick)}%`,
                right: 0,
                transform: 'translateY(-50%)',
                fontSize: 10,
                color: token.colorTextTertiary,
              }}
            >
              {tick}
            </span>
          ))}
        </div>
        <div
          style={{ position: 'relative', flex: 1, height }}
          onMouseMove={handleMove}
          onMouseLeave={() => setHover(null)}
        >
          <svg
            viewBox="0 0 100 100"
            width="100%"
            height={height}
            preserveAspectRatio="none"
            role="img"
            aria-label={ariaLabel}
          >
            {TICKS.map((tick) => (
              <line
                key={tick}
                x1={0}
                x2={100}
                y1={yOf(tick)}
                y2={yOf(tick)}
                stroke={token.colorBorderSecondary}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {hovered ? (
              <line
                x1={xOf(hovered.index, quarters.length)}
                x2={xOf(hovered.index, quarters.length)}
                y1={0}
                y2={100}
                stroke={token.colorBorder}
                strokeDasharray="2 2"
                vectorEffect="non-scaling-stroke"
              />
            ) : null}
            <path
              d={path}
              fill="none"
              stroke={color}
              strokeWidth={2}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          {placed.map(({ quarter, index, point }) =>
            point === null ? null : (
              <span
                key={quarter}
                style={{
                  position: 'absolute',
                  left: `${xOf(index, quarters.length)}%`,
                  top: `${yOf(point.average)}%`,
                  width: hover === index ? 10 : 7,
                  height: hover === index ? 10 : 7,
                  borderRadius: '50%',
                  background: color,
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none',
                }}
              />
            ),
          )}
          {hovered ? (
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                background: token.colorBgElevated,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusSM,
                padding: '4px 8px',
                boxShadow: token.boxShadowSecondary,
                pointerEvents: 'none',
              }}
            >
              <Typography.Text strong style={{ fontSize: 12, display: 'block' }}>
                {quarterLabel(hovered.quarter)}
              </Typography.Text>
              <Typography.Text style={{ fontSize: 12 }}>
                {hovered.point === null ? '—' : hovered.point.average.toFixed(2)}
              </Typography.Text>{' '}
              {hovered.point === null ? null : (
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {countLabel(hovered.point.ratingCount)}
                </Typography.Text>
              )}
            </div>
          ) : null}
        </div>
      </div>
      <div style={{ position: 'relative', height: 14, marginInlineStart: 22 }}>
        {quarters.map((quarter, index) =>
          tickIndexes.has(index) ? (
            <span
              key={quarter}
              style={{
                position: 'absolute',
                left: `${xOf(index, quarters.length)}%`,
                transform: 'translateX(-50%)',
                fontSize: 10,
                color: token.colorTextTertiary,
                whiteSpace: 'nowrap',
              }}
            >
              {quarterLabel(quarter)}
            </span>
          ) : null,
        )}
      </div>
    </div>
  )
}
